/* [서비스 가치(Service Value)] 글로벌 Context 데이터와 컴포넌트 재사용성을 극대화한 SPA 엔트리 포인트.
   🚀 CTO 패치: 
   1. 메뉴명 동적 분기: 'CareReportManager'를 학생/학부모에게는 [출결 상황]으로, 강사/관리자에게는 [원생별 출결관리]로 분리하여 노출합니다.
   2. 통합 출결 관리 최적화: '통합 출결 관리'의 설명을 일별 관제 및 매트릭스 전용으로 수정했습니다.
   3. 온톨로지 맵 통합: 'AI 수학 지식 맵'을 Lazy Loading으로 주입하여 초기 로딩 속도(LCP) 저하 없이 상담용 시각화 툴을 제공합니다. */

import React, { useState, Suspense, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { 
  Home, Calendar as CalendarIcon, Settings, LogOut, Menu, X, CheckCircle, Eye, EyeOff, AlertCircle, 
  Video, Loader, DollarSign, Briefcase, Printer, BookOpen, User, Target, Compass, FileText, Activity,
  Clock, Trash2, MessageSquare, Globe, Phone, Search, Clipboard, Book, Users, Star, ChevronDown, ChevronRight,
  PieChart, UserPlus, UserCheck, Brain, GraduationCap, Sparkles, CalendarDays, Share2, ClipboardList, ShoppingBag, Boxes, KeyRound, Plus
} from 'lucide-react'; // 🚀 CTO Patch: 온톨로지 맵을 위한 Share2 아이콘 추가

import { doc, getDoc, terminate, clearIndexedDbPersistence, waitForPendingWrites } from 'firebase/firestore';
import { signInWithEmailAndPassword, signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import { DataProvider, useData } from './contexts/DataContext';
import SmartSchoolSelect from './components/SmartSchoolSelect';
import ErrorBoundary from './components/ErrorBoundary';
import CampusSwitcher from './components/CampusSwitcher';
import NewVersionBanner from './components/NewVersionBanner';
import { toStorableSchoolName } from './utils/schoolName';
import { toMainSubject, MAIN_SUBJECTS } from './utils/subjectMatch';
import { toSafeId } from './utils/onboarding';
import { APP_ID } from './constants';
import TodaySummary from './features/TodaySummary';   // 대시보드 '오늘' 요약 — 첫 화면이라 지연 로딩하지 않는다
import emblemWhite from './assets/emblem-white.png';   // 학원 날개 엠블럼(흰색) — 파란 사각형 안에 넣는 로고

// 컴포넌트 Lazy 로딩
const ClinicDashboard = React.lazy(() => import('./features/ClinicDashboard'));
const ClinicTaskManager = React.lazy(() => import('./features/ClinicTaskManager'));
const CallRecords = React.lazy(() => import('./features/CallRecords'));
const AdminLectureManager = React.lazy(() => import('./features/LectureManager').then(module => ({ default: module.AdminLectureManager })));
const LecturerDashboard = React.lazy(() => import('./features/LectureManager').then(module => ({ default: module.LecturerDashboard })));
const StudentClassroom = React.lazy(() => import('./features/StudentClassroom'));
const UserManager = React.lazy(() => import('./features/UserManager'));
const PayrollManager = React.lazy(() => import('./features/PayrollManager'));
const PickupRequest = React.lazy(() => import('./features/PickupRequest'));
const ExamArchive = React.lazy(() => import('./features/ExamArchive'));
const TextbookManager = React.lazy(() => import('./features/TextbookManager'));
const HomeworkManager = React.lazy(() => import('./features/HomeworkManager'));
const SchoolStrategy = React.lazy(() => import('./features/SchoolStrategy'));
const ExamDiagnosticInput = React.lazy(() => import('./features/ExamDiagnosticInput'));
const ExamDiagnosticReport = React.lazy(() => import('./features/ExamDiagnosticReport'));
const StudentExamList = React.lazy(() => import('./features/StudentExamList'));
const ExpenseManager = React.lazy(() => import('./features/ExpenseManager'));
const FinancialDashboard = React.lazy(() => import('./features/FinancialDashboard'));
const AttendanceManager = React.lazy(() => import('./features/AttendanceManager'));
const SettingsManager = React.lazy(() => import('./features/SettingsManager'));
const MessageCenter = React.lazy(() => import('./features/MessageCenter'));
const CollegeNavigator = React.lazy(() => import('./features/CollegeNavigator'));
const AcademyUniverse = React.lazy(() => import('./features/AcademyUniverse'));
const ConsultationManager = React.lazy(() => import('./features/ConsultationManager'));
const VocaManager = React.lazy(() => import('./features/VocaManager'));
const StudentVocaDaily = React.lazy(() => import('./features/StudentVocaDaily'));
const VocaChallenge = React.lazy(() => import('./features/VocaChallenge'));
const StudentDashboard = React.lazy(() => import('./features/StudentDashboard'));
const RecruitmentManager = React.lazy(() => import('./features/RecruitmentManager'));
const AcademicCalendarManager = React.lazy(() => import('./features/AcademicCalendarManager'));
const CareReportManager = React.lazy(() => import('./features/CareReportManager'));
// 교재 구매 연동: 학생·학부모는 [내 교재], 교직원은 [교재 주문 관리]
const BookOrders = React.lazy(() => import('./features/BookOrders'));
// 교재 발주 관리: 결제된 교재를 모아 서점에 주문하고 도착·입고까지 기록 (지금은 관리자만 — src/utils/bookPurchases.js PURCHASE_ROLES)
const BookPurchases = React.lazy(() => import('./features/BookPurchases'));
// 🚀 CTO Patch: OntologyMap Lazy 로딩 추가 (초기 로딩 최적화로 이탈률 0% 방어)
const OntologyMap = React.lazy(() => import('./features/ontology/OntologyMap'));
// 📊 성적 분석 허브: 연동 진단 + 분석 스튜디오 14기능을 하위 탭으로 (관리자 전용)
// ⚠ 로컬 점검용 이식 (2026-08-23) — docs/admission-center/PLAN.md 승인 전까지 커밋·배포 금지
const GradeAnalysisHub = React.lazy(() => import('./features/GradeAnalysisHub'));


/* 로그인 아이디 → Firebase Auth 이메일 앞부분(toSafeId)은 utils/onboarding.js 의 것을 씁니다.
   예전에는 여기에 사본이 따로 있었습니다. 서버(functions/authCore.js)와 글자 하나까지 같아야 하는 규칙이라,
   사본을 하나 줄이고 그 파일의 시험(npm run test:onboarding)이 서버 정의와 직접 대조하게 했습니다. */

/* 급여 화면이 예전에 브라우저 저장소(localStorage)에 남기던 그 달 급여 사본(imperial_payroll_v8_…)을 지웁니다.
   이름·실수령액·공제·시급이 들어 있는데 로그아웃해도 지워지지 않아, 원장이 쓰던 PC 를 다음에 쓰는 사람이
   볼 수 있었습니다(행정조교가 주소로 열었던 공용 PC 포함). 로그아웃할 때와 앱을 열 때 한 번씩 지웁니다. */
const clearPayrollCache = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('imperial_payroll_')) keys.push(k);
    }
    keys.forEach(k => localStorage.removeItem(k));
  } catch (e) {
    // 저장소를 쓸 수 없는 브라우저(사생활 보호 모드 등)는 남은 사본도 없습니다.
  }
};

/* 공용 PC 로그아웃 — 이 브라우저에 남은 Firestore 사본(IndexedDB)을 지웁니다.
   이 앱은 오프라인 사본(firebase.js 의 persistentLocalCache)을 켜 두어서, 화면이 읽은 문서가 로그아웃 뒤에도
   브라우저에 그대로 남았습니다. 원장이 데스크 PC 에서 사용자 관리·급여 화면을 열었다 로그아웃하면, 다음에 같은
   브라우저를 쓰는 사람이 개발자 도구로 전 직원 시급·월급·계좌 사본을 읽을 수 있었습니다(보안 규칙이 막는 바로 그 자료).
   지우려면 이 탭의 Firestore 를 먼저 멈춰야(terminate) 하고, 멈춘 Firestore 는 다시 쓸 수 없으므로 지운 뒤 페이지를 새로 엽니다.
   같은 브라우저의 다른 탭은 지우는 순간 Firestore 가 멈춥니다(SDK 동작). 그래서 다 지운 뒤 그 탭들에게도 새로 열라고
   알립니다(CACHE_CLEARED_SIGNAL_KEY). 응답하지 않는 탭이 사본을 붙잡고 있으면 지우기가 끝나지 않으므로 시간 제한을 둡니다. */
const CACHE_CLEAR_FAILED_KEY = 'imperial_cache_clear_failed';   // sessionStorage: 지우지 못했음 → 다음 로그인 화면에서 알림
const CACHE_CLEARED_SIGNAL_KEY = 'imperial_cache_cleared_at';   // localStorage: 다른 탭에게 '사본을 지웠으니 새로 열라'는 신호

const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('TIMEOUT')), ms);
    promise.then(
        (v) => { clearTimeout(timer); resolve(v); },
        (e) => { clearTimeout(timer); reject(e); }
    );
});

/** 이 탭의 Firestore 를 멈추고 브라우저 사본을 지웁니다. 지웠으면 true. 부른 뒤에는 반드시 페이지를 새로 열어야 합니다. */
const clearLocalFirestoreCache = async () => {
    try {
        await withTimeout(terminate(db), 5000);
        await withTimeout(clearIndexedDbPersistence(db), 5000);
        return true;
    } catch (e) {
        console.warn('이 기기에 저장된 자료를 지우지 못했습니다:', e?.code || e?.message || e);
        return false;
    }
};

/* 인터넷이 끊긴 채 페이지를 새로 열면(로그아웃 뒤 로그인 화면으로 replace, 신호를 받은 다른 탭의 reload) 브라우저의
   '인터넷 연결 없음' 오류 화면으로 바뀝니다. 이 앱은 서비스 워커를 끄고 있어(index.js 의 unregister) 화면 파일을 매번
   서버에서 받기 때문입니다. Chrome 은 연결되면 그 화면을 저절로 다시 불러오지만, Safari·카카오톡 인앱 브라우저는 사람이
   직접 새로고침해야 합니다. 그렇다고 지금 화면에 머물 수도 없습니다 — Firestore 를 멈췄으므로(terminate) 그대로 다시
   로그인하면 사용자 정보를 읽지 못해 '아이디·비밀번호 확인'으로 잘못 안내됩니다.
   그래서 navigator.onLine 이 false 이면 다시 연결될 때(online 이벤트, 한 번만) 엽니다. 바로 열었으면 true, 기다리는 중이면 false
   (그동안의 안내는 부른 쪽이 띄웁니다). onLine 이 true 라고 인터넷이 꼭 되는 것은 아니지만, 그때는 예전처럼 바로 엽니다.
   새로 연 페이지는 신호를 다시 보내지 않으므로(신호는 사람이 누른 로그아웃·[지금 지우기]만 보냄) 새로고침이 되풀이되지 않습니다. */
const openWhenOnline = (go) => {
    if (navigator.onLine === false) {
        window.addEventListener('online', () => go(), { once: true });
        return false;
    }
    go();
    return true;
};
// 새로 열기를 미루는 동안 보이는 안내의 뒷부분. 앞부분(무엇을 했는지)은 부른 곳마다 다릅니다 — 지우지 못했으면 지웠다고 쓰지 않습니다.
const OFFLINE_REOPEN_NOTICE = '인터넷 연결이 끊겨 로그인 화면을 열 수 없습니다 — 연결되면 자동으로 엽니다.';

/** 지우기 결과를 새로 연 페이지(로그인 화면)에 넘기고, 다른 탭에 신호를 보낸 뒤 로그인 화면으로 새로 엽니다.
    바로 열었으면 true, 인터넷이 끊겨 연결을 기다리는 중이면 false 입니다(openWhenOnline — 그동안의 안내는 부른 쪽이 띄움). */
const finishCacheClearAndReload = (cleared) => {
    try {
        if (cleared) sessionStorage.removeItem(CACHE_CLEAR_FAILED_KEY);
        else sessionStorage.setItem(CACHE_CLEAR_FAILED_KEY, '1');
    } catch (_) { /* 저장소를 못 쓰는 브라우저 — 안내만 빠집니다 */ }
    try {
        // 값이 바뀔 때만 다른 탭에 storage 이벤트가 가므로 시각을 씁니다. 바로 지워도 '썼다'는 이벤트는 이미 나갔습니다.
        localStorage.setItem(CACHE_CLEARED_SIGNAL_KEY, String(Date.now()));
        localStorage.removeItem(CACHE_CLEARED_SIGNAL_KEY);
    } catch (_) { /* 저장소를 못 쓰면 다른 탭은 직접 새로고침해야 합니다 */ }
    return openWhenOnline(() => window.location.replace('/'));
};

const readCacheClearFailed = () => {
    try { return sessionStorage.getItem(CACHE_CLEAR_FAILED_KEY) === '1'; } catch (_) { return false; }
};

// 메뉴 생성 함수 (권한에 따라 동적으로 메뉴명을 할당합니다)
const buildMenuGroups = (currentUser) => [
    {
        title: "나의 학원 생활",
        description: "오늘의 스케줄과 AI 브리핑을 확인합니다.",
        theme: "from-indigo-600 to-blue-700",
        items: [
            { name: "My Imperial Day", path: "/my-day", icon: Sparkles, desc: "오늘의 완벽한 스케줄과 AI 멘토링", roles: ['student'] },
            { name: "출결 상황", path: "/care-report", icon: PieChart, desc: "학원의 밀착 케어 내역과 출결 통계", roles: ['student', 'parent'] }
        ]
    },
    {
        title: "교무 및 출결 관제",
        description: "클리닉 예약, 출결 현황 및 교실 공간을 관리합니다.",
        theme: "from-blue-600 to-indigo-700",
        items: [
            { name: "신규 상담 등록", path: "/consult", icon: UserPlus, desc: "신규 원생 상담 데이터를 입력합니다.", roles: ['admin', 'admin_assistant', 'lecturer'] },
            { name: "통합 출결 관리", path: "/attendance", icon: UserCheck, desc: "당일 출결 관제 및 교실 매트릭스", roles: ['admin', 'admin_assistant', 'lecturer'] },
            { name: "원생별 출결관리", path: "/care-report", icon: PieChart, desc: "원생별 누적 학습 시간 및 출결 데이터", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "학사일정 마스터", path: "/academic-calendar", icon: CalendarDays, desc: "학교별 학사일정 및 D-Day 관리", roles: ['admin', 'admin_assistant'] },
            { name: "클리닉 센터", path: "/clinic", icon: CalendarIcon, desc: "1:N 클리닉 배정 및 예약 현황", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "오늘의 할 일", path: "/clinic-tasks", icon: Clipboard, desc: "조교 업무 지시 및 진행률 트래킹", roles: ['admin', 'admin_assistant', 'ta', 'lecturer'] },
            { name: "픽업 신청", path: "/pickup", icon: Printer, desc: "학생 픽업을 요청하고 관리합니다.", roles: ['lecturer'] }
        ]
    },
    {
        title: "학습 및 수강",
        description: "학생들의 성적, 수업, 학습 자료를 관리합니다.",
        theme: "from-emerald-600 to-teal-700",
        items: [
            { name: "강의 관리", studentName: "수강 강의", path: "/lectures", icon: Video, studentIcon: GraduationCap, desc: "수업 진도, 숙제 관리 및 영상 시청", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            /* 학생·학부모의 [내 교재] (반에 지정된 교재 안내 → 북스에서 결제). 교직원용 주문·발주 관리는 아래 '북스 관리' 묶음에 있다. */
            { name: "내 교재", path: "/books", icon: ShoppingBag, studentIcon: ShoppingBag, desc: "수업 교재 확인 · 구매 · 수령 현황", roles: ['student', 'parent'] },
            { name: "내신 연구소", path: "/strategy", icon: Brain, desc: "학교별 맞춤형 출제 경향 및 리포트", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            // 🚀 CTO Patch: 상담 전환율을 극대화하는 AI 수학 지식 맵 메뉴 추가 (모든 역할군 오픈)
            { name: "AI 수학 지식 맵", path: "/ontology", icon: Share2, desc: "수학 커리큘럼 선행 및 취약점 시각화", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "기출 아카이브", path: "/exams", icon: BookOpen, desc: "학교별 기출문제 은행", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "교재 관리", path: "/textbooks", icon: Book, desc: "문제집 범위·단원 등록 (숙제 배정용)", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "숙제 관리", path: "/homework", icon: ClipboardList, desc: "교재에서 숙제 출제 및 오답 채점", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "Voca 출제/관리", studentName: "오늘의 영단어", path: "/voca", icon: Book, desc: "맞춤형 단어장 및 고속 채점 시스템", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'], condition: (u) => !['lecturer', 'ta'].includes(u?.role) || toMainSubject(u?.subject) === '영어' },
            { name: "영단어 챌린지", path: "/voca-challenge", icon: Star, desc: "게이미피케이션 기반 단어 암기", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student'] },
            { name: "시험 진단 입력", path: "/exam-diagnostics", icon: Target, desc: "시험 결과를 입력하고 리포트 생성", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "나의 시험 결과", path: "/my-exams", icon: Target, desc: "성적표 및 담당 선생님 리포트", roles: ['student', 'parent'] }
        ]
    },
    {
        // ⚠ 로컬 점검용 상위 탭 (2026-08-23) — PLAN 승인 전까지 커밋·배포 금지
        // 사이드바 항목이 곧 기능이다 — 순서는 상담 흐름(학생 → 성적 → 진단 → 생기부 → 리포트).
        title: "진로진학",
        description: "입시 내비게이터와 내신·모의고사 연동 진단, 수시·정시 전략을 한 화면에서.",
        theme: "from-violet-600 to-indigo-700",
        items: [
            /* 원장 지시(08-23): 성적입력·학생관리는 기존 시스템이 하고, 대시보드·모의고사
               분석은 제외(모의고사 내용은 추후 기존 성적입력 화면에서 활용 예정).
               생기부 진단·OMR 채점 탭도 제외.
               탭 재편(08-24 원장 승인 B안): 11개 → 5개. 수시·정시·전문대는 '지원 진단'
               상단 전환으로, 등급 변환기는 그 안의 도구 버튼으로, 탐구+과목은 '학종 설계'로,
               등급컷+성취도는 '기준 데이터'로 묶음(GradeAnalysisHub GROUPS). 일정 캘린더는
               학사일정 마스터의 '입시 일정' 레이어 + 수시 진단 카트의 '일정 보기'로 이동.
               08-24: 그룹명 성적 분석 → 진로진학, 입시 내비게이터를 학습 그룹에서 이리로
               옮겨 첫 항목으로 (학생·학부모 접근 권한은 그대로). */
            /* 08-24 원장 지시: 성적 연동 진단 탭 삭제(입시 내비게이터가 그 역할),
               지원 진단 → 시뮬레이터로 개명. 내비게이터에서 학생을 고르면 그 학생이
               스튜디오(시뮬레이터·학종 설계·종합 리포트)의 활성 학생으로 자동 연동된다. */
            /* 원장 지시(08-30): 진로진학 탭 전체를 관리자 계정만 — 새 진단 DB·추천이 검수 전이라
               학생·학부모에게는 아직 적용하지 않는다. 검수가 끝나면 student·parent 를 되돌린다. */
            { name: "입시 내비게이터", path: "/navigator", icon: Compass, desc: "성적 추이 분석 및 목표 대학 전략", roles: ['admin'] },
            { name: "시뮬레이터", path: "/grade-center/apply", icon: Target, desc: "수시·정시·전문대 판정 + 등급 변환기", roles: ['admin'] },
            { name: "학종 설계", path: "/grade-center/roadmap", icon: Book, desc: "탐구 주제 DB · 고교학점제 과목 설계", roles: ['admin'] },
            { name: "기준 데이터", path: "/grade-center/data", icon: Clipboard, desc: "모의고사 등급컷 · 성취도 반영 정책", roles: ['admin'] },
            { name: "종합 리포트", path: "/grade-center/report", icon: Printer, desc: "학부모 면담용 한 부 인쇄", roles: ['admin'] }
        ]
    },
    {
        title: "재무 및 행정",
        description: "수납, 급여, 시스템 설정을 관리합니다.",
        theme: "from-amber-500 to-orange-600",
        items: [
            { name: "재무 대시보드", path: "/financial-dashboard", icon: PieChart, desc: "자금 흐름 파악 및 결재 승인", roles: ['admin'] },
            { name: "지출결의 등록", path: "/expense", icon: FileText, desc: "지출 내역 등록 및 증빙 업로드", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "월급 정산 관리", path: "/payroll-mgmt", icon: Briefcase, desc: "전체 직원의 급여 정산 및 관리", roles: ['admin'] },
            { name: "내 월급 확인", path: "/payroll-check", icon: DollarSign, desc: "이번 달 급여 명세서 확인", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "사용자 관리", path: "/users", icon: Users, desc: "모든 계정 승인 및 권한 관리", roles: ['admin', 'admin_assistant'] },
            { name: "채용 및 인사 관리", path: "/recruitment", icon: Briefcase, desc: "조교/강사 채용 및 온보딩 파이프라인", roles: ['admin', 'admin_assistant'] }
        ]
    },
    {
        /* 임페리얼 북스(별도 사업자) 업무. 결제는 북스 사이트에서 하고, 학원 시스템은 주문 전달·발주·입고만 관리한다.
           교재 발주 관리는 지금 관리자만 — 데스크 실장님·행정조교에게 열 때 roles 와 PURCHASE_ROLES·firestore.rules 를 함께 바꾼다. */
        title: "북스 관리",
        description: "임페리얼 북스 교재 주문 전달·발주·입고를 관리합니다.",
        theme: "from-amber-700 to-stone-900",
        items: [
            { name: "교재 주문 관리", path: "/books", icon: ShoppingBag, desc: "반별 교재 구매 현황 · 주문 전달 · 환불 · 정산", roles: ['admin', 'admin_assistant'] },
            { name: "교재 발주 관리", path: "/book-purchases", icon: Boxes, desc: "주문할 교재 모아 보기 · 발주 · 도착 · 입고 관리", roles: ['admin'] }
        ]
    },
    {
        title: "학원 생활 및 시스템",
        description: "알림, 메시지, 스케줄을 확인합니다.",
        theme: "from-slate-700 to-slate-900",
        items: [
            { name: "아카데미 유니버스", path: "/universe", icon: Globe, desc: "학원 소식 및 커뮤니티 랭킹", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "통합 메시지 센터", path: "/messages", icon: MessageSquare, desc: "알림톡/SMS 발송 및 관리", roles: ['admin', 'admin_assistant'] },
            { name: "통화 기록", path: "/call-records", icon: Phone, desc: "법인폰 통화 자동 요약 (원장 전용)", roles: ['admin'] },
            { name: "근무 스케줄", path: "/work-schedule", icon: Clock, desc: "나의 근무 일정 확인 및 변경 요청", roles: ['admin_assistant'] },
            { name: "환경 설정", path: "/settings", icon: Settings, desc: "학원 시스템 코어 데이터 설정", roles: ['admin'] }
        ]
    }
];

/* 학생·학부모에게는 교직원용 묶음 이름 대신 쉬운 이름을 보인다(11-02 학생·학부모 점검 —
   클리닉 센터 하나가 '교무 및 출결 관제' 아래에 있었다). 사이드바와 대시보드가 같이 쓴다. */
const FAMILY_GROUP_TITLES = { '교무 및 출결 관제': '클리닉', '학원 생활 및 시스템': '학원 소식' };
const getMenuGroups = (currentUser) => {
    const groups = buildMenuGroups(currentUser);
    if (!['student', 'parent'].includes(currentUser?.role)) return groups;
    return groups.map(g => (FAMILY_GROUP_TITLES[g.title] ? { ...g, title: FAMILY_GROUP_TITLES[g.title] } : g));
};

const ReportWrapper = () => {
  const { diagnosticId } = useParams();
  return <ExamDiagnosticReport diagnosticId={diagnosticId} />;
};


const SignUpForm = ({ onCancel, setLoginErrorModal }) => {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false); 
    const [form, setForm] = useState({ role: 'student', userId: '', password: '', name: '', phone: '', schoolName: '', grade: '1학년', childName: '', subject: '' });
    const [smsAuth, setSmsAuth] = useState({ ticket: '', input: '', sent: false, verified: false, timer: 0 });
    const [schoolsData, setSchoolsData] = useState({ elementary: [], middle: [], high: [], favorites: [] });
    const [schoolType, setSchoolType] = useState('high');
    const [isCustomSchool, setIsCustomSchool] = useState(false); 

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const docRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'schools');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) setSchoolsData(docSnap.data());
            } catch (e) { console.error(e); }
        };
        fetchSchools();
    }, []);

    useEffect(() => {
        let interval = null;
        if (smsAuth.timer > 0 && !smsAuth.verified) {
            interval = setInterval(() => { setSmsAuth(prev => ({ ...prev, timer: prev.timer - 1 })); }, 1000);
        } else if (smsAuth.timer === 0 && smsAuth.sent && !smsAuth.verified) {
            setSmsAuth(prev => ({ ...prev, sent: false }));
            setLoginErrorModal({ isOpen: true, msg: "인증 시간이 만료되었습니다. 다시 시도해주세요." });
        }
        return () => clearInterval(interval);
    }, [smsAuth.timer, smsAuth.sent, smsAuth.verified, setLoginErrorModal]);

    /* 🔒 인증번호는 서버가 발급하고 서버가 채점합니다.
       (기존에는 브라우저가 번호를 만들고 브라우저가 비교해서 우회가 가능했습니다) */
    const handleSendAuthCode = async () => {
        const cleanPhone = form.phone.replace(/[^0-9]/g, '');
        if (cleanPhone.length < 10) return setLoginErrorModal({ isOpen: true, msg: '유효한 휴대폰 번호를 입력해주세요.' });
        setLoading(true);
        try {
            const requestCode = httpsCallable(functions, 'requestSignupCode');
            await requestCode({ phone: cleanPhone, name: form.name || '신규가입자' });
            setSmsAuth({ ticket: '', input: '', sent: true, verified: false, timer: 180 });
            alert('인증번호가 발송되었습니다.');
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: '인증번호 발송 실패: ' + (error.message || '잠시 후 다시 시도해주세요.') });
        } finally { setLoading(false); }
    };

    const handleVerifyCode = async () => {
        const cleanPhone = form.phone.replace(/[^0-9]/g, '');
        setLoading(true);
        try {
            const verifyCode = httpsCallable(functions, 'verifySignupCode');
            const res = await verifyCode({ phone: cleanPhone, code: smsAuth.input });
            if (res?.data?.verified) {
                setSmsAuth(prev => ({ ...prev, verified: true, ticket: res.data.ticket }));
            } else {
                setLoginErrorModal({ isOpen: true, msg: '인증번호가 일치하지 않습니다.' });
            }
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: error.message || '인증번호가 일치하지 않습니다.' });
        } finally { setLoading(false); }
    };

    const getGradeOptions = (type) => {
        if (type === 'elementary') return ['1학년','2학년','3학년','4학년','5학년','6학년'];
        if (type === 'middle') return ['1학년','2학년','3학년'];
        return ['1학년','2학년','3학년','N수생'];
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        if (!smsAuth.verified) return setLoginErrorModal({ isOpen: true, msg: '먼저 휴대폰 본인 인증을 완료해주세요.' });
        if (!form.userId || !form.password || !form.name) return setLoginErrorModal({ isOpen: true, msg: '필수 정보를 모두 입력해주세요.' });
        if (form.password.length < 6) return setLoginErrorModal({ isOpen: true, msg: '비밀번호는 6자리 이상이어야 합니다.' });
        if (['student', 'parent'].includes(form.role) && !form.schoolName) return setLoginErrorModal({ isOpen: true, msg: '학교를 정확히 선택하거나 입력해주세요.' });
        if (form.role === 'parent' && !form.childName) return setLoginErrorModal({ isOpen: true, msg: '자녀 이름을 입력해주세요.' });
        if (['ta', 'lecturer'].includes(form.role) && !form.subject) return setLoginErrorModal({ isOpen: true, msg: '담당 과목을 선택해주세요.' });

        setLoading(true);
        try {
            /* 🔒 계정 생성은 서버가 담당합니다.
               비밀번호는 Firebase Auth에만 저장되고 Firestore에는 남지 않습니다. */
            const register = httpsCallable(functions, 'registerUser');
            await register({
                ticket: smsAuth.ticket,
                phone: form.phone.replace(/[^0-9]/g, ''),
                userId: form.userId,
                password: form.password,
                name: form.name,
                role: form.role,
                // 마스터 목록에 있는 학교면 그 정식 명칭으로 통일해 저장합니다.
                schoolName: toStorableSchoolName(form.schoolName, schoolsData),
                grade: form.grade,
                childName: form.childName,
                subject: form.subject
            });
            alert('가입 신청이 완료되었습니다. 데스크 승인 후 로그인 가능합니다.');
            onCancel();
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: error.message || '가입 처리 중 오류가 발생했습니다.' });
        } finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSignUp} className="space-y-4 animate-in fade-in">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">시스템 회원가입</h2>
                <button type="button" onClick={onCancel} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"><X size={20}/></button>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">가입 유형</label>
                <select className="w-full border rounded-xl p-3 bg-gray-50 font-bold" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                    {/* 교직원(강사·조교) 계정은 직접 가입할 수 없습니다.
                        예전에는 여기서 '강사'를 고르면 서버가 그대로 믿고 교직원 권한을 줬습니다.
                        교직원 계정은 데스크가 [직원 관리]에서 발급합니다. */}
                    <option value="student">학생</option>
                    <option value="parent">학부모</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5 ml-1">
                    강사·조교 계정은 학원 데스크에서 발급해 드립니다.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">이름</label>
                    <input required className="w-full border p-3 rounded-xl bg-gray-50 font-bold" placeholder="실명 입력" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">아이디</label>
                    <input required className="w-full border p-3 rounded-xl bg-gray-50 font-bold" placeholder="영문/숫자" value={form.userId} onChange={e => setForm({...form, userId: e.target.value})} />
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">비밀번호</label>
                <div className="relative">
                    <input required type={showPassword ? "text" : "password"} placeholder="6자리 이상" className="w-full border p-3 rounded-xl bg-gray-50 font-bold" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
                    <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 space-y-3">
                <label className="block text-xs font-bold text-blue-800 flex items-center gap-1"><Phone size={14}/> 휴대폰 본인 인증</label>
                <div className="flex gap-2">
                    <input className="w-full border p-3 rounded-xl font-bold bg-white" placeholder="01012345678 (-없이)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} disabled={smsAuth.verified} />
                    <button type="button" onClick={handleSendAuthCode} disabled={loading || smsAuth.verified || smsAuth.timer > 0} className="shrink-0 bg-blue-600 text-white rounded-xl font-bold px-4">{smsAuth.timer > 0 ? '재전송' : '인증번호 받기'}</button>
                </div>
                {smsAuth.sent && !smsAuth.verified && (
                    <div className="flex gap-2 animate-in slide-in-from-top-2">
                        <div className="relative w-full">
                            <input className="w-full border-2 border-indigo-200 p-3 rounded-xl font-black text-center tracking-widest bg-white" placeholder="인증번호 6자리" value={smsAuth.input} onChange={e => setSmsAuth({...smsAuth, input: e.target.value})} />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-500 font-bold text-sm flex items-center gap-1"><Clock size={14}/> {Math.floor(smsAuth.timer / 60)}:{String(smsAuth.timer % 60).padStart(2, '0')}</div>
                        </div>
                        <button type="button" onClick={handleVerifyCode} className="shrink-0 bg-indigo-600 text-white rounded-xl font-bold px-4">확인</button>
                    </div>
                )}
                {smsAuth.verified && <div className="text-sm font-bold text-emerald-600 flex items-center gap-1"><CheckCircle size={16}/> 인증이 완료되었습니다.</div>}
            </div>

            {['student', 'parent'].includes(form.role) && (
                <div className="grid grid-cols-1 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    {form.role === 'parent' && (
                        <div className="mb-2">
                            <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">자녀 이름</label>
                            <input required className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" placeholder="자녀 실명 입력" value={form.childName} onChange={e => setForm({...form, childName: e.target.value})} />
                            <p className="text-[11px] text-rose-500 font-bold ml-1 mt-1.5">* 2명 이상의 자녀가 재원 중인 경우, 가입 후 데스크에 문의 바랍니다.</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">{form.role === 'parent' ? '자녀 학교 정보' : '학교 정보'}</label>
                        <div className="flex gap-2 relative">
                            <select className="w-1/3 border-2 border-gray-200 p-3 rounded-xl bg-white font-bold text-sm outline-none focus:border-blue-400 transition-colors" value={schoolType} onChange={e => { setSchoolType(e.target.value); setForm({...form, schoolName: '', grade: '1학년'}); setIsCustomSchool(false); }}>
                                <option value="elementary">초등학교</option>
                                <option value="middle">중학교</option>
                                <option value="high">고등학교</option>
                            </select>
                            {/* 회원가입은 로그인 전이라 학교 마스터 목록에 쓸 권한이 없습니다.
                                그래서 여기서는 '새 학교로 추가'를 막고 기존 직접 입력을 남기되,
                                저장 시점에 목록의 정식 명칭으로 맞춥니다(handleSignUp 참고). */}
                            {!isCustomSchool ? (
                                <SmartSchoolSelect size="lg" className="w-2/3" allowAddNew={false} schoolType={schoolType} schoolsData={schoolsData} value={form.schoolName} onChange={(val) => setForm({...form, schoolName: val})} onCustomSelect={() => setIsCustomSchool(true)}/>
                            ) : (
                                <div className="w-2/3 relative">
                                    <input required className="w-full border-2 border-blue-400 p-3 rounded-xl bg-white font-bold text-sm pr-8 outline-none" placeholder="학교명 직접 입력" value={form.schoolName} onChange={e => setForm({...form, schoolName: e.target.value})} />
                                    <button type="button" onClick={() => { setIsCustomSchool(false); setForm({...form, schoolName: ''}); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-white rounded-full p-1 transition-colors"><X size={16}/></button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">{form.role === 'parent' ? '자녀 학년' : '학년'}</label>
                        <select className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" value={form.grade} onChange={e => setForm({...form, grade: e.target.value})}>
                            {getGradeOptions(schoolType).map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {['ta', 'lecturer'].includes(form.role) && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">담당 과목</label>
                    <select required className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}>
                        <option value="" disabled hidden>과목을 선택해주세요</option>
                        {/* 회원 관리·반 과목과 같은 어휘를 씁니다.
                            예전에는 여기만 '기타' 가 있어서, 그걸 고른 강사는 어느 과목으로도
                            판정되지 않는 상태로 남았습니다. 사회는 아예 없었습니다.
                            가입 화면은 로그인 전이라 부서 설정을 읽을 수 없으므로 다섯 개를 모두 보여주고,
                            실제 확정은 회원 관리에서 합니다. */}
                        {MAIN_SUBJECTS.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                </div>
            )}

            <div className="pt-2">
                <button type="submit" disabled={loading || !smsAuth.verified} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-md hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {loading ? <Loader className="animate-spin mx-auto" /> : '가입 신청 완료하기'}
                </button>
            </div>
        </form>
    );
};

const LoginView = ({ form, setForm, onLogin, isLoading, loginErrorModal, setLoginErrorModal, cacheClearFailed, cacheClearBlocked, onRetryCacheClear, onDismissCacheNotice }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const handleKeyDown = (e) => { if (e.key === 'Enter') onLogin(); };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-xl p-8 border border-gray-100">
        {isSignUpMode ? (
            <SignUpForm onCancel={() => setIsSignUpMode(false)} setLoginErrorModal={setLoginErrorModal} />
        ) : (
            <>
                <div className="text-center mb-8">
                    <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <img src={emblemWhite} alt="" draggable={false} className="w-11 h-auto select-none" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Imperial System</h1>
                    <p className="text-gray-500 mt-2">목동임페리얼학원 통합 관리 시스템</p>
                </div>
                {/* 직전 로그아웃이 이 브라우저의 저장 자료(Firestore 사본)를 지우지 못했을 때만 보입니다(clearLocalFirestoreCache 참고).
                    예전 문구는 '다시 로그아웃해 주세요'였는데, 로그인 화면에서는 할 수 없는 동작이라 이 상자의 버튼으로 안내합니다. */}
                {cacheClearFailed && (
                    <div className="mb-6 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl p-3 text-sm font-bold leading-relaxed break-keep space-y-2">
                        <div className="flex gap-2">
                            <AlertCircle size={18} className="shrink-0 mt-0.5 text-amber-600" />
                            <span>다른 탭이 열려 있어 이 기기에 저장된 자료를 지우지 못했습니다. 공용 PC 라면 이 탭만 남기고 모든 탭을 닫은 뒤 [지금 지우기]를 누르세요.</span>
                        </div>
                        {/* [지금 지우기]를 눌렀지만 이 브라우저에 로그인한 사람이 있어 지우지 않았을 때(handleRetryCacheClear 참고). */}
                        {cacheClearBlocked && (
                            <p role="alert" className="pl-6 text-rose-700">
                                이 브라우저의 다른 탭에 로그인한 사람이 있습니다 — 그 탭에서 로그아웃한 뒤 다시 누르세요.
                                <span className="block mt-1 text-xs text-amber-800">그 탭을 이미 닫았다면, 여기서 로그인했다가 로그아웃하면 다시 지웁니다.</span>
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2 pl-6">
                            <button type="button" onClick={onRetryCacheClear} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700">다른 탭을 모두 닫았습니다 — 지금 지우기</button>
                            <button type="button" onClick={onDismissCacheNotice} className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 text-xs font-bold hover:bg-amber-100">닫기</button>
                        </div>
                    </div>
                )}
                <div className="space-y-5">
                    {/* 휴대폰이 첫 글자를 대문자로 바꾸거나 자동 고침을 하지 않게 합니다.
                        초기 비밀번호는 소문자·숫자라서, 'Abcd…' 로 바뀌면 맞는 비밀번호를 넣고도 로그인이 실패합니다. */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">아이디</label>
                        <input type="text" placeholder="ID를 입력하세요" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full border rounded-xl p-4 bg-gray-50 outline-none focus:border-blue-500 font-bold" value={form.id} onChange={e=>setForm({...form, id:e.target.value})}/>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">비밀번호</label>
                        <div className="relative">
                            <input type={showPassword ? "text" : "password"} placeholder="비밀번호를 입력하세요" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full border rounded-xl p-4 bg-gray-50 outline-none focus:border-blue-500 font-bold" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} onKeyDown={handleKeyDown}/>
                            <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={24} /> : <Eye size={24} />}</button>
                        </div>
                    </div>
                    <button type="button" onClick={onLogin} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-md transition hover:bg-blue-700 active:bg-blue-800 active:scale-[0.99] disabled:opacity-60 disabled:cursor-wait" disabled={isLoading}>
                        {isLoading ? <Loader className="animate-spin mx-auto" /> : '로그인'}
                    </button>
                    <div className="pt-2 text-center border-t border-gray-100">
                        <button type="button" onClick={() => setIsSignUpMode(true)} className="text-sm font-bold text-blue-600 hover:text-blue-800">새로 오셨나요? 회원가입 하기</button>
                    </div>
                </div>
            </>
        )}
      </div>
      {loginErrorModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in">
            <div className="bg-white p-6 rounded-2xl max-w-sm w-full shadow-2xl text-center space-y-4">
                <div className="bg-red-50 p-4 rounded-full text-red-500 inline-block"><AlertCircle size={48} /></div>
                <h3 className="text-xl font-bold leading-relaxed whitespace-pre-wrap">{loginErrorModal.msg}</h3>
                <button className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold" onClick={() => setLoginErrorModal({ isOpen: false, msg: '' })}>확인</button>
            </div>
         </div>
      )}
    </div>
  );
};

/* 첫 로그인 비밀번호 변경 화면.
   데스크·서버가 정해 준 비밀번호(상담 온보딩 문자, [비번 변경] 재발급, 새로 만든 직원 계정)로 들어온 사람은
   users 문서에 mustChangePassword: true 가 있습니다. 서버만 쓰는 값이라 본인이 지울 수 없습니다.
   예전에는 이런 장치가 없었고, 본인이 비밀번호를 바꿀 화면조차 없었습니다. 그래서 문자로 받은 비밀번호
   (그리고 그것을 계산할 수 있던 학부모 번호·출결 PIN)를 아는 사람은 누구나 계속 로그인할 수 있었습니다.

   비밀번호는 서버(changeInitialPassword)가 바꿉니다. 브라우저에서 바꾸면 '최근 로그인 필요' 오류가 나고,
   실제로 바뀌었는지 서버가 알 수 없어 표시를 지울 근거가 없기 때문입니다.
   서버가 비밀번호를 바꾸면 이 기기의 로그인 갱신 토큰도 끊기므로, 곧바로 새 비밀번호로 다시 로그인합니다.
   이 표시는 새 서버만 세웁니다 — 옛 서버로 만든 계정에는 이 화면이 뜨지 않습니다. */
const FirstPasswordChange = ({ currentUser, onDone, onLogout }) => {
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [canSkip, setCanSkip] = useState(false);   // 지금은 바꿀 방법이 없을 때만 true(아래 handleSkip 참고)
    const loginId = currentUser?.userId || currentUser?.id || '';

    /* [이번 접속만 건너뛰기] — 비밀번호를 바꿀 서버 기능에 닿지 못하거나, 서버가 이 계정의 사용자 정보를
       찾지 못하거나 이 계정 것이 아니라고 답할 때(어느 쪽이든 데스크·개발 담당이 고치기 전에는 바꿀 수 없음),
       그리고 시도 횟수를 넘겨 서버가 한동안 변경을 받지 않을 때만 보입니다.
       예전에는 이때 로그아웃 말고 할 수 있는 것이 없었습니다. 그래서 서버 함수를 되돌리거나(롤백) 배포가 일부만 되면,
       표시가 선 학생·직원 전원이 시스템에 들어오지 못했습니다(이 표시는 서버만 지울 수 있습니다).
       서버의 표시는 건드리지 않고 화면의 사본만 바꿉니다. 다음에 새로 로그인하면 users 문서를 다시 읽어 이 화면이 또 뜹니다.
       이 화면은 보안 규칙이 아니라 화면에서만 막는 장치라, 건너뛰어도 권한은 그대로입니다. */
    const handleSkip = () => onDone({ ...currentUser, mustChangePassword: false });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setError('');
        setCanSkip(false);
        // 길이·확인만 여기서 봅니다. 아이디·번호 포함 같은 나머지 규칙은 서버가 판정하고 그 문구를 그대로 보여 줍니다.
        if (pw.length < 8) return setError('새 비밀번호는 8자 이상이어야 합니다.');
        if (pw.length > 64) return setError('새 비밀번호는 64자 이하로 정해 주세요.');
        if (pw !== pw2) return setError('두 칸에 입력한 비밀번호가 서로 다릅니다. 다시 확인해 주세요.');

        // 다시 로그인할 때 쓸 이메일. 서버가 비밀번호를 바꾸기 전에 받아 둡니다.
        const email = auth.currentUser?.email || '';
        /* 이 화면을 띄운 문서(로그인 때 읽은 users 문서)의 ID 도 함께 보냅니다. 서버는 그 문서가 이 로그인 계정과
           연결돼 있을 때만 그 문서의 표시를 봅니다. 예전에는 서버가 토큰의 did 문서만 봐서, 한 계정을 두 문서가 가리키면
           표시가 없는 쪽을 읽고 '이미 변경되었습니다' → 로그아웃 → 같은 비밀번호로 다시 이 화면, 이 반복에서 빠져나오지 못했습니다.
           옛 서버는 docId 를 모르고 무시합니다. */
        const request = { newPassword: pw };
        if (currentUser?.id) request.docId = String(currentUser.id);
        setBusy(true);
        try {
            await httpsCallable(functions, 'changeInitialPassword')(request);
        } catch (err) {
            setBusy(false);
            const code = err?.code || '';
            if (code === 'functions/failed-precondition') {
                alert('이미 변경되었습니다 — 새 비밀번호로 다시 로그인해 주세요.');
                onLogout();
                return;
            }
            /* 서버는 비밀번호를 (다시) 발급하기 전에 시작된 로그인으로는 바꾸지 못하게 합니다 — 데스크가 재발급해 끊어 낸
               예전 세션이 새 비밀번호를 정해 버리는 것을 막기 위해서입니다. 로그인이 끊긴 경우도 같은 오류로 옵니다. */
            if (code === 'functions/unauthenticated') {
                alert('새로 받은 비밀번호로 다시 로그인해 주세요.\n(이 화면을 연 뒤에 비밀번호가 다시 발급됐거나 로그인이 끊겨, 지금 로그인으로는 바꿀 수 없습니다.)');
                onLogout();
                return;
            }
            /* 서버 함수가 아예 없으면(배포 전·되돌린 뒤) 브라우저는 서버 응답을 못 받고, 오류 문구가 코드 이름
               그대로('internal'·'not-found'·'unavailable')인 오류만 남습니다. 인터넷이 끊겨도 똑같이 보여 둘을 가를 수 없으므로 둘 다 적습니다.
               서버가 직접 낸 오류는 한국어 문구가 있습니다(예: 사용자 정보 없음, 규칙 위반). 그 문구는 그대로 보여 줍니다. */
            const sdkOnly = err?.message === code.replace('functions/', '');
            if (sdkOnly && ['functions/internal', 'functions/not-found', 'functions/unavailable'].includes(code)) {
                setError('비밀번호 변경 서버에 연결하지 못했습니다. 인터넷 연결을 확인하고 다시 눌러 주세요.\n계속 안 되면 학원 데스크에 알려 주세요(서버 기능이 아직 배포되지 않았을 수 있습니다).');
                setCanSkip(true);
                return;
            }
            /* 서버가 이 계정의 사용자 정보를 찾지 못했거나(not-found), 이 로그인 계정과 연결된 문서가 아니라고 답했습니다
               (permission-denied). 데스크가 사용자 정보를 고치기 전에는 여기서 바꿀 방법이 없어, 이번 접속만 건너뛸 수 있게 합니다. */
            if (code === 'functions/not-found' || code === 'functions/permission-denied') {
                setError(sdkOnly ? '이 계정의 사용자 정보를 서버에서 확인하지 못했습니다. 학원 데스크에 문의해 주세요.' : (err?.message || '사용자 정보를 찾을 수 없습니다. 데스크에 문의해주세요.'));
                setCanSkip(true);
                return;
            }
            /* 시도 횟수 초과(resource-exhausted) — 서버가 한동안(계정당 1시간) 이 계정의 변경을 받지 않습니다.
               예전에는 서버 문구만 보여 줘서 로그아웃 말고 할 수 있는 것이 없었고, 원장이 [변경 강제 해제]를 누를 때까지
               앱에 들어오지 못했습니다. 그 횟수는 이 사람이 아니라 초기화로 끊긴 예전 세션이 써 버렸을 수도 있습니다.
               서버 문구(언제 다시 되는지)를 그대로 보여 주고 이번 접속만 건너뛸 수 있게 합니다 — 이 화면은 보안 규칙이 아니라
               화면에서만 막는 장치라 건너뛰어도 권한은 그대로입니다. 서버 문구 없이 오면(요청 폭주 등 응답 코드만 온 경우) 일반 안내를 씁니다. */
            if (code === 'functions/resource-exhausted') {
                setError(sdkOnly ? '지금은 요청이 많아 비밀번호를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.' : (err?.message || '비밀번호 변경 시도가 너무 많습니다. 1시간 뒤에 다시 시도해 주세요.'));
                setCanSkip(true);
                return;
            }
            if (sdkOnly || ['INTERNAL', 'UNKNOWN'].includes(err?.message)) {
                setError('서버 오류로 비밀번호를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.');
                return;
            }
            // 규칙 위반(아이디·번호 포함 등)·저장 일부 실패는 서버가 한국어로 이유와 할 일을 보냅니다.
            setError(err?.message || '비밀번호를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.');
            return;
        }

        /* 여기부터는 서버에서 이미 바뀌었습니다.
           세션 복원(onAuthStateChanged)은 sessionStorage 의 프로필을 다시 읽으므로, 다시 로그인하기 전에 먼저 고쳐 둡니다.
           그러지 않으면 다시 로그인하는 순간 옛 표시(mustChangePassword: true)가 되살아나 이 화면이 또 뜹니다. */
        const updated = { ...currentUser, mustChangePassword: false };
        try { sessionStorage.setItem('imperial_user', JSON.stringify(updated)); } catch (_) { /* 저장소 없음 — onDone 이 상태로 넘깁니다 */ }
        try {
            if (!email) throw new Error('NO_EMAIL');
            await signInWithEmailAndPassword(auth, email, pw);
        } catch (err) {
            // 새 비밀번호로 다시 들어오지 못했습니다. 지금 토큰은 곧 끊기므로 직접 다시 로그인하게 합니다.
            console.warn('비밀번호 변경 뒤 재로그인 실패:', err?.code || err?.message);
            // 로그아웃이 '저장 안 된 변경' 확인에서 취소되면 이 화면에 머뭅니다. 버튼이 잠긴 채 남지 않게 풀어 둡니다.
            setBusy(false);
            alert('비밀번호를 바꿨습니다. 새 비밀번호로 다시 로그인해 주세요.');
            onLogout();
            return;
        }
        onDone(updated);
    };

    const inputClass = "w-full border rounded-xl p-4 bg-gray-50 outline-none focus:border-blue-500 font-bold";
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <form onSubmit={handleSubmit} className="bg-white w-full max-w-md rounded-3xl shadow-xl p-6 sm:p-8 border border-gray-100 space-y-5">
                <div className="text-center">
                    <div className="bg-blue-600 text-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <KeyRound size={30} />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">새 비밀번호 정하기</h1>
                    <p className="text-gray-500 mt-2 text-sm leading-relaxed break-keep">
                        {currentUser?.name ? `${currentUser.name}님, ` : ''}지금 쓰신 비밀번호는 학원이 발급한 초기 비밀번호입니다.
                        문자 등으로 전달돼 다른 사람도 알 수 있으므로, 본인만 아는 비밀번호로 바꾼 뒤에 시스템을 쓸 수 있습니다.
                    </p>
                </div>

                {/* 비밀번호 관리 프로그램이 새 비밀번호를 이 아이디로 저장하게 알려 주는 칸입니다(화면에는 안 보임). */}
                <input type="text" name="username" autoComplete="username" value={loginId} readOnly tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} />

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">새 비밀번호</label>
                    <div className="relative">
                        <input type={showPw ? 'text' : 'password'} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                            placeholder="8자 이상" className={inputClass} value={pw} onChange={e => setPw(e.target.value)} disabled={busy} />
                        <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPw(v => !v)} aria-label={showPw ? '비밀번호 가리기' : '비밀번호 보기'}>
                            {showPw ? <EyeOff size={22} /> : <Eye size={22} />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">새 비밀번호 확인</label>
                    <input type={showPw ? 'text' : 'password'} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                        placeholder="한 번 더 입력" className={inputClass} value={pw2} onChange={e => setPw2(e.target.value)} disabled={busy} />
                </div>

                <ul className="text-xs font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1 leading-relaxed">
                    <li>· 8자 이상으로 정해 주세요.</li>
                    <li>· 아이디{loginId ? `(${loginId})` : ''}나 휴대폰 번호가 들어가면 안 됩니다.</li>
                    <li>· 같은 글자만 반복하면 안 됩니다.</li>
                </ul>

                {error && (
                    <div className="bg-rose-50 text-rose-700 border border-rose-200 rounded-xl p-3 text-sm font-bold whitespace-pre-wrap flex gap-2">
                        <AlertCircle size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
                    </div>
                )}

                <button type="submit" disabled={busy} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-md disabled:opacity-50">
                    {busy ? <Loader className="animate-spin mx-auto" /> : '비밀번호 바꾸고 시작하기'}
                </button>
                {canSkip && !busy && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                        <p className="text-xs font-bold text-amber-900 leading-relaxed break-keep">
                            지금은 비밀번호를 바꿀 수 없어 이번 접속만 건너뛸 수 있습니다. 비밀번호는 그대로이고, 다음에 로그인하면 이 화면이 다시 나옵니다.
                        </p>
                        <button type="button" onClick={handleSkip} className="w-full py-3 bg-white border-2 border-amber-300 text-amber-800 rounded-xl font-bold hover:bg-amber-100">
                            이번 접속만 건너뛰기
                        </button>
                    </div>
                )}
                <button type="button" onClick={onLogout} disabled={busy} className="w-full flex items-center justify-center gap-2 text-sm font-bold text-gray-500 hover:text-red-500 py-2 disabled:opacity-50">
                    <LogOut size={16} /> 로그아웃
                </button>
            </form>
        </div>
    );
};


/* [대시보드] 첫 화면 — 오늘 요약(TodaySummary) → My 메뉴 → 전체 메뉴 아이콘 격자.
   11-02 개편: 예전에는 큰 인사 배너 + 메뉴를 높이 160px 카드로 다시 그린 것뿐이라
   휴대폰에서 화면 7.5장 길이에 정보가 없었다. 인사는 한 줄로 줄이고, 메뉴는 아이콘 격자로
   (설명은 사이드바와 같은 내용이라 뺐다 — 버튼 title 로만 남김). */

/* My 메뉴 — 자주 쓰는 메뉴를 사람마다 골라 대시보드 맨 위에 둔다(11-02 원장 결정: 하단 탭바 대신).
   ⚠ 지금은 이 기기의 브라우저에만 저장한다. 회원 문서에 저장하려면 firestore.rules 의
     users 본인 수정 허용 칸(hasOnly 목록)에 새 칸을 더해 규칙을 배포해야 해서, 그 결정 전까지는 기기별로 둔다.
     PC 와 휴대폰에서 각각 고르게 되는 것이 그 대가다. */
const MY_MENU_KEY = (uid) => `imperial_my_menu_${uid}`;
const loadMyMenu = (uid) => {
    try { const v = JSON.parse(localStorage.getItem(MY_MENU_KEY(uid)) || '[]'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
};

const MenuIcon = ({ item, theme, isFamily, onClick, mark }) => {
    const SafeIcon = ((item.studentIcon && isFamily) ? item.studentIcon : item.icon) || Activity;
    const label = (item.studentName && isFamily) ? item.studentName : item.name;
    return (
        <button
            type="button"
            title={item.desc}
            onClick={onClick}
            className="group relative flex flex-col items-center gap-1.5 px-1 py-2 rounded-2xl transition hover:bg-white active:bg-slate-200/60 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${theme} text-white shadow-sm transition-transform group-hover:scale-105`}>
                <SafeIcon size={22} />
            </span>
            {mark && (
                <span className={`absolute top-1 left-1/2 ml-3 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow ${mark === 'on' ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {mark === 'on' ? <Star size={12} fill="currentColor" /> : <Plus size={13} strokeWidth={3} />}
                </span>
            )}
            <span className="text-xs font-bold text-slate-700 text-center leading-snug break-keep line-clamp-2">{label}</span>
        </button>
    );
};

const ICON_GRID = 'grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-x-1 gap-y-3';

const Dashboard = ({ currentUser }) => {
    const navigate = useNavigate();
    const { loadingData } = useData() || { loadingData: false };
    const [myPaths, setMyPaths] = useState(() => (currentUser?.id ? loadMyMenu(currentUser.id) : []));
    const [editingMy, setEditingMy] = useState(false);

    const authorizedGroups = useMemo(() => {
        if (!currentUser?.role) return [];
        const dynamicGroups = getMenuGroups(currentUser);

        return dynamicGroups.map(group => {
            const authorizedItems = group.items.filter(item => {
                const hasRole = item.roles.includes(currentUser.role);
                const passCondition = item.condition ? item.condition(currentUser) : true;
                return hasRole && passCondition;
            });
            return { ...group, items: authorizedItems };
        }).filter(group => group.items.length > 0);
    }, [currentUser]);

    // 경로 → 메뉴(색 포함). 권한이 바뀌어 더는 못 쓰는 메뉴는 My 메뉴에서도 저절로 빠진다.
    const itemByPath = useMemo(() => {
        const m = new Map();
        authorizedGroups.forEach(g => g.items.forEach(item => { if (!m.has(item.path)) m.set(item.path, { item, theme: g.theme }); }));
        return m;
    }, [authorizedGroups]);
    const myItems = myPaths.map(p => itemByPath.get(p)).filter(Boolean);

    const toggleMy = (path) => {
        setMyPaths(prev => {
            const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
            try { localStorage.setItem(MY_MENU_KEY(currentUser.id), JSON.stringify(next)); } catch (e) { /* 저장 공간이 막힌 브라우저 — 이번 접속 동안만 유지 */ }
            return next;
        });
    };

    if (loadingData || !currentUser) {
        return (
            <div className="flex flex-col justify-center items-center h-[50vh]">
                <Loader className="animate-spin text-indigo-500 mb-3" size={32} />
                <p className="text-slate-500 font-bold text-sm">불러오는 중...</p>
            </div>
        );
    }

    const today = new Date();
    const todayLabel = `${today.getMonth() + 1}월 ${today.getDate()}일 ${['일', '월', '화', '수', '목', '금', '토'][today.getDay()]}요일`;
    const isFamily = ['student', 'parent'].includes(currentUser.role);

    return (
        <div className="max-w-screen-2xl mx-auto space-y-5 md:space-y-7 animate-in fade-in pb-20">
            <div className="px-1">
                <p className="text-xs md:text-sm font-bold text-slate-400">{todayLabel}</p>
                <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">안녕하세요, {currentUser.name}님</h1>
            </div>

            <TodaySummary currentUser={currentUser} />

            <section className={editingMy ? 'rounded-2xl ring-2 ring-amber-300 bg-amber-50/60 p-2 -m-2' : ''}>
                <div className="flex items-center px-1 mb-2">
                    <h2 className="text-sm font-black text-slate-800 flex items-center gap-1"><Star size={14} className="text-amber-400" fill="currentColor" /> My 메뉴</h2>
                    <button type="button" onClick={() => setEditingMy(v => !v)}
                        className={`ml-auto px-3 py-2 rounded-lg text-xs font-bold ${editingMy ? 'bg-amber-500 text-white' : 'text-slate-500 hover:bg-white active:bg-slate-200'}`}>
                        {editingMy ? '완료' : '편집'}
                    </button>
                </div>
                {editingMy && <p className="text-xs font-bold text-amber-700 px-1 mb-2">아래 메뉴의 + 를 눌러 추가하고, ★ 를 눌러 뺍니다. 이 기기에 저장됩니다.</p>}
                {myItems.length > 0 ? (
                    <div className={ICON_GRID}>
                        {myItems.map(({ item, theme }) => (
                            <MenuIcon key={item.path} item={item} theme={theme} isFamily={isFamily}
                                mark={editingMy ? 'on' : null}
                                onClick={() => (editingMy ? toggleMy(item.path) : navigate(item.path))} />
                        ))}
                    </div>
                ) : !editingMy && (
                    <button type="button" onClick={() => setEditingMy(true)}
                        className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 text-sm font-bold text-slate-400 hover:bg-white active:bg-slate-100">
                        자주 쓰는 메뉴를 골라 여기에 모아 두세요
                    </button>
                )}
            </section>

            <div className="space-y-5 md:space-y-6">
                {authorizedGroups.map((group, gIdx) => (
                    <section key={gIdx}>
                        <h2 className="text-sm font-black text-slate-500 px-1 mb-2">{group.title}</h2>
                        <div className={ICON_GRID}>
                            {group.items.map((item, iIdx) => (
                                <MenuIcon key={iIdx} item={item} theme={group.theme} isFamily={isFamily}
                                    mark={editingMy ? (myPaths.includes(item.path) ? 'on' : 'off') : null}
                                    onClick={() => (editingMy ? toggleMy(item.path) : navigate(item.path))} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};

const AppLayout = ({ currentUser, handleLogout }) => {
  const { users } = useData();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const groupedMenus = useMemo(() => {
      const groups = {};
      const dynamicGroups = getMenuGroups(currentUser);
      dynamicGroups.forEach(group => {
          const items = group.items.filter(item => {
              const hasRole = item.roles.includes(currentUser.role);
              const passCondition = item.condition ? item.condition(currentUser) : true;
              return hasRole && passCondition;
          });
          
          if (items.length > 0) {
              groups[group.title] = items.map(item => ({
                  path: item.path,
                  label: (item.studentName && ['student', 'parent'].includes(currentUser.role)) ? item.studentName : item.name,
                  icon: (item.studentIcon && ['student', 'parent'].includes(currentUser.role)) ? item.studentIcon : item.icon,
              }));
          }
      });
      return groups;
  }, [currentUser]);

  const [expandedCategories, setExpandedCategories] = useState({});
  useEffect(() => {
      const initials = {};
      Object.keys(groupedMenus).forEach(cat => initials[cat] = true);
      setExpandedCategories(initials);
  }, [groupedMenus]);

  const toggleCategory = (category) => {
      setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden w-full">
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden animate-in fade-in duration-300" onClick={() => setIsSidebarOpen(false)}/>}
      
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-300 md:relative md:translate-x-0 flex flex-col ${isSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          {/* 모바일 머리글과 같은 모양 — 파란색은 엠블럼 사각형 하나에만 둡니다. */}
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
              <img src={emblemWhite} alt="" draggable={false} className="w-6 h-auto select-none" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">Imperial</h1>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"><X /></button>
        </div>
        {/* 캠퍼스 전환 — 고를 캠퍼스가 둘 이상인 교직원에게만 보입니다(지금은 원장). */}
        <div className="px-4 pt-3 empty:hidden"><CampusSwitcher /></div>
        
        <nav className="p-3 space-y-4 flex-1 overflow-y-auto custom-scrollbar">
            <button 
                onClick={() => { navigate('/dashboard'); setIsSidebarOpen(false); }} 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${location.pathname === '/dashboard' ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-700 hover:bg-gray-50 font-medium'}`}
            >
                <Home size={20} /> 대시보드 홈
            </button>

            {Object.entries(groupedMenus).map(([category, items]) => (
                <div key={category} className="space-y-1">
                    <button 
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between px-4 py-2 text-xs font-black text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {category}
                        {expandedCategories[category] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                    </button>
                    
                    {expandedCategories[category] && (
                        <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                            {items.map((item) => {
                                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                const SafeIcon = item.icon || Activity;
                                return (
                                    <button 
                                        key={item.path} 
                                        onClick={() => { navigate(item.path); setIsSidebarOpen(false); }} 
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${isActive ? 'bg-blue-50 text-blue-600 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <SafeIcon size={18} className={isActive ? 'text-blue-600' : 'text-gray-400'} /> 
                                        <span className="text-sm">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
        </nav>
        
        <div className="p-4 border-t bg-white shrink-0 z-10 mt-auto">
            <div className="flex items-center gap-3 mb-4 px-2 p-2 rounded-xl border border-transparent">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-600 uppercase">{currentUser?.name?.[0] || 'U'}</div>
                <div className="flex flex-col text-left flex-1">
                    <span className="font-bold text-sm text-gray-900 leading-tight">{currentUser?.name || '사용자'}</span>
                    <span className="text-xs text-gray-500 uppercase">
                        {currentUser?.role === 'admin_assistant' ? 'ADMIN ASSISTANT' : currentUser?.role}
                    </span>
                </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-2 text-red-500 hover:bg-red-50 p-2 rounded-lg font-bold transition-colors">
                <LogOut size={16}/> 로그아웃
            </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-full overflow-hidden w-full relative min-w-0">
        <NewVersionBanner />
        <header className="md:hidden shrink-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between shadow-sm z-30">
            <div className="flex items-center gap-2">
                <div className="bg-blue-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
                    <img src={emblemWhite} alt="" draggable={false} className="w-6 h-auto select-none" />
                </div>
                <h1 className="text-lg font-bold text-gray-900">Imperial</h1>
            </div>
            <CampusSwitcher compact />
            <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all active:scale-95" aria-label="메뉴 열기">
                <Menu size={24} />
            </button>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 w-full bg-gray-50">
            {/* ⚠️ 예전에는 여기에 flex flex-col items-stretch 가 붙어 있었습니다.
                그런데 각 화면의 최상위 요소는 거의 모두 'max-w-*xl mx-auto' 형태입니다.
                CSS 규칙상 flex 항목의 좌우 margin 이 auto 이면 stretch(늘리기)가 꺼지고,
                항목이 '내용 크기'대로 커집니다. 그래서 내용이 넓은 화면(재무 대시보드,
                환경설정 등)은 최상위 컨테이너 자체가 화면보다 넓어졌고,
                main 의 overflow-x-hidden 에 잘려 오른쪽이 안 보였습니다.
                (진단 결과: 화면 384px인데 컨테이너가 671px)
                평범한 블록으로 두면 자식은 부모 폭을 넘지 않습니다. */}
            <div className="max-w-[1600px] w-full mx-auto px-3 sm:px-4 md:px-8 py-4 md:py-6">
                {/* 화면 하나가 터져도 앱 전체가 흰 화면이 되지 않게 막습니다.
                    주소가 바뀌면(resetKey) 오류 상태가 저절로 풀립니다. */}
                <ErrorBoundary label={location.pathname} resetKey={location.pathname}>
                <Suspense fallback={<div className="h-full flex items-center justify-center min-h-[50vh]"><Loader className="animate-spin text-blue-600" size={40} /></div>}>
                    <Routes>
                        <Route path="/dashboard" element={<Dashboard currentUser={currentUser} />} />
                        <Route path="/my-day" element={currentUser.role === 'student' ? <StudentDashboard currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/consult" element={['admin', 'admin_assistant', 'lecturer'].includes(currentUser.role) ? <ConsultationManager /> : <Navigate to="/dashboard" replace />} />
                        {['admin', 'lecturer', 'admin_assistant'].includes(currentUser.role) && <Route path="/attendance" element={<AttendanceManager currentUser={currentUser} />} />}
                        <Route path="/lectures" element={ ['admin', 'admin_assistant'].includes(currentUser.role) ? <AdminLectureManager /> : currentUser.role === 'lecturer' ? <LecturerDashboard currentUser={currentUser} /> : <StudentClassroom currentUser={currentUser} /> } />
                        <Route path="/call-records" element={currentUser.role === 'admin' ? <CallRecords currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/messages" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <MessageCenter currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/users" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <UserManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/settings" element={currentUser.role === 'admin' ? <SettingsManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/financial-dashboard" element={currentUser.role === 'admin' ? <FinancialDashboard currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/expense" element={<ExpenseManager currentUser={currentUser} />} />}
                        <Route path="/strategy" element={<SchoolStrategy currentUser={currentUser} />} />
                        
                        {/* 🚀 CTO Patch: 온톨로지 라우트 연결 (어느 권한이든 로그인된 유저면 확인 가능) */}
                        <Route path="/ontology" element={<OntologyMap />} />

                        <Route path="/clinic" element={<ClinicDashboard currentUser={currentUser} users={users} mode="clinic" />} />
                        <Route path="/clinic-tasks" element={['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) ? <ClinicTaskManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {currentUser.role === 'admin_assistant' && <Route path="/work-schedule" element={<ClinicDashboard currentUser={currentUser} users={users} mode="work_schedule" />} />}
                        <Route path="/pickup" element={<PickupRequest currentUser={currentUser} />} />
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/exams" element={<ExamArchive currentUser={currentUser} />} />}
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/textbooks" element={<TextbookManager currentUser={currentUser} />} />}
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/homework" element={<HomeworkManager currentUser={currentUser} />} />}
                        {/* 월급 정산 관리(전 직원 급여·계좌)는 원장만. 예전에는 메뉴만 원장에게 보이고 주소는 로그인한 누구나 열 수 있었습니다. */}
                        <Route path="/payroll-mgmt" element={currentUser.role === 'admin' ? <PayrollManager currentUser={currentUser} users={users} viewMode="management" /> : <Navigate to="/dashboard" replace />} />
                        {/* 내 월급 확인은 교직원 네 역할만 — 메뉴와 같은 범위. 학생·학부모에게는 볼 명세가 없습니다. */}
                        <Route path="/payroll-check" element={['admin', 'admin_assistant', 'lecturer', 'ta'].includes(currentUser.role) ? <PayrollManager currentUser={currentUser} users={users} viewMode="personal" /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/exam-diagnostics" element={['admin', 'lecturer', 'admin_assistant', 'ta'].includes(currentUser.role) ? <ExamDiagnosticInput currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/report/:diagnosticId" element={<ReportWrapper />} />
                        <Route path="/my-exams" element={['student', 'parent'].includes(currentUser.role) ? <StudentExamList currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/voca" element={['student', 'parent'].includes(currentUser.role) ? <StudentVocaDaily currentUser={currentUser} /> : <VocaManager currentUser={currentUser} />} />
                        <Route path="/voca-challenge" element={['student', 'admin', 'admin_assistant', 'lecturer', 'ta'].includes(currentUser.role) ? <VocaChallenge currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 원장 지시(08-30): 검수 전이라 관리자만 — 검수 후 student·parent 복원 */}
                        <Route path="/navigator" element={['admin'].includes(currentUser.role) ? <CollegeNavigator currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/navigator/:studentId" element={['admin'].includes(currentUser.role) ? <CollegeNavigator currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 📊 성적 분석 — 로컬 점검용 이식 (관리자 전용, 사이드바 항목 = 기능) */}
                        <Route path="/grade-center" element={['admin'].includes(currentUser.role) ? <GradeAnalysisHub /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/grade-center/:tab" element={['admin'].includes(currentUser.role) ? <GradeAnalysisHub /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/universe" element={['student', 'parent', 'admin', 'admin_assistant', 'lecturer', 'ta'].includes(currentUser.role) ? <AcademyUniverse currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        
                        <Route path="/recruitment" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <RecruitmentManager /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/academic-calendar" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <AcademicCalendarManager /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/care-report" element={<CareReportManager currentUser={currentUser} />} />
                        {/* 교재 주문: 데스크(관리자·행정조교)와 학생·학부모만. 강사·조교는 주문(학부모 연락처 포함)을 볼 필요가 없다. */}
                        <Route path="/books" element={['admin', 'admin_assistant', 'student', 'parent'].includes(currentUser.role) ? <BookOrders currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 교재 발주 관리: 지금은 관리자만 (나중에 데스크·행정조교에게 열 예정) */}
                        <Route path="/book-purchases" element={currentUser.role === 'admin' ? <BookPurchases currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />

                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </Suspense>
                </ErrorBoundary>
            </div>
        </main>
      </div>
    </div>
  );
};

/* 상담 키오스크의 가상 사용자. 컴포넌트 밖에 두어야 화면을 다시 그릴 때마다
   새 객체가 되어 DataProvider 가 구독을 끊었다 다시 거는 일이 없다. */
const KIOSK_USER = { role: 'admin_assistant', name: '상담용 태블릿 기기' };

const AppContent = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ id: '', password: '' });
  const [loginProcessing, setLoginProcessing] = useState(false);
  const [loginErrorModal, setLoginErrorModal] = useState({ isOpen: false, msg: '' });
  /* 로그아웃(또는 로그인 화면의 [지금 지우기])으로 이 기기의 사본을 지우는 중이면 그 안내 문구('' = 아님).
     지운 뒤 인터넷이 끊겨 새로 열기를 기다리는 동안(다른 탭의 신호를 받은 경우 포함)의 안내도 여기에 둡니다.
     이 동안은 앱 화면을 내리고 안내만 보입니다 — Firestore 를 멈춘(terminate) 뒤 새로 열기 전까지
     어떤 화면도 Firestore 를 부르면 안 되기 때문입니다. 한 번 서면 되돌리지 않습니다(새로 열어야만 풀림). */
  const [clearingText, setClearingText] = useState('');
  const loggingOutRef = useRef(false);   // 두 번 누름·다른 탭 신호와 겹치지 않게
  const [cacheClearFailed, setCacheClearFailed] = useState(readCacheClearFailed);
  const [cacheClearBlocked, setCacheClearBlocked] = useState(false);   // [지금 지우기]를 눌렀지만 이 브라우저에 로그인한 사람이 있어 지우지 않음

  const navigate = useNavigate();

  // 앱을 열 때 한 번, 예전 급여 화면이 남긴 브라우저 사본을 지웁니다(clearPayrollCache 설명 참고).
  useEffect(() => { clearPayrollCache(); }, []);

  /* 같은 브라우저의 다른 탭이 로그아웃하며 사본을 지웠다는 신호를 받으면 이 탭도 새로 엽니다.
     지우는 순간 이 탭의 Firestore 는 SDK 가 멈춰 버려서, 그대로 두면 이 탭에서 다시 로그인할 때 사용자 정보를 읽다가
     영영 멈춘 채 돕니다. 신호를 보내는 두 곳(로그아웃, 로그인 화면의 [지금 지우기]) 모두 이 브라우저에 로그인한 사람이
     없을 때만 지우므로(로그인 상태는 탭끼리 공유 — handleRetryCacheClear 참고) 이 탭도 이미 로그아웃된 상태이고,
     새로 열어도 잃는 입력은 없습니다. 주소는 그대로 둡니다(상담 태블릿 /kiosk/consult 는 그 화면으로 다시 열림).
     인터넷이 끊겨 있으면 연결될 때 새로 엽니다(openWhenOnline). 그동안은 앱 화면을 내리고 안내만 둡니다 — 멈춘 Firestore 로
     로그인하려다 잘못된 안내를 받지 않게. loggingOutRef 를 세워 기다리는 동안 또 오는 신호는 넘깁니다(연결되면 한 번만 새로 엶). */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== CACHE_CLEARED_SIGNAL_KEY || !e.newValue || loggingOutRef.current) return;
      if (openWhenOnline(() => window.location.reload())) return;
      loggingOutRef.current = true;
      setClearingText(`다른 탭에서 로그아웃해 이 화면도 새로 열어야 합니다. ${OFFLINE_REOPEN_NOTICE}`);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /* 🔒 세션 복원은 Firebase Auth 세션을 기준으로 합니다.
     이제 모든 Firestore 접근이 인증을 요구하므로, sessionStorage만 남아 있고
     Auth 세션이 끊긴 상태로 앱에 들어가면 화면 전체가 권한 오류로 깨집니다.
     따라서 Auth 세션이 없으면 저장된 프로필도 함께 버립니다. */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const savedUser = sessionStorage.getItem('imperial_user');
      if (firebaseUser && savedUser) {
        try {
          /* 이미 로그인된 상태로 앱을 다시 열었을 때도 역할 토큰을 최신화합니다.
             역할이 바뀌었거나 토큰에 역할이 없던 사용자를 자동으로 복구합니다. */
          try { await firebaseUser.getIdToken(true); } catch (e) { console.warn('토큰 갱신 실패:', e); }

          /* 첫 로그인 비밀번호 변경 표시(mustChangePassword)도 여기 함께 실려 있습니다 — 로그인 때 users 문서에서
             읽어 넣었습니다(handleLogin). 그 뒤 다른 기기에서 비밀번호를 바꿨거나 데스크가 재발급했다면 서버가
             이 기기의 갱신 토큰을 끊으므로, 바로 위 getIdToken(true) 가 실패해 로그아웃되고 다시 로그인하면서 새 값을 읽습니다. */
          const parsed = JSON.parse(savedUser);
          if (parsed.id) parsed.id = String(parsed.id).toLowerCase();
          setCurrentUser(parsed);
        } catch (e) {
          sessionStorage.removeItem('imperial_user');
          setCurrentUser(null);
        }
      } else {
        sessionStorage.removeItem('imperial_user');
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  /* 🔒 로그인은 반드시 Firebase Auth 세션을 확보한 뒤에 프로필을 읽습니다.
     1) 정상 계정  : Auth로 바로 로그인
     2) 과거 계정  : 서버(legacyLoginBridge)가 대조 후 Auth 계정을 만들어 주고
                     Firestore의 평문 비밀번호를 즉시 삭제 → 자동 이관
     어느 경로든 최종적으로 Auth 세션이 있어야만 앱에 들어옵니다. */
  const handleLogin = async () => {
      if (!loginForm.id || !loginForm.password) { setLoginErrorModal({ isOpen: true, msg: '정보를 입력하세요.' }); return; }
      setLoginProcessing(true);
      try {
          const rawId = loginForm.id.trim();
          const safeId = toSafeId(rawId);
          let docId = safeId;

          /* ⚠️ 과거 호환: Firebase Auth는 비밀번호가 6자리 이상이어야 합니다.
             예전 코드는 6자리 미만 비밀번호를 '0'으로 채워서 Auth에 저장했기 때문에,
             그런 계정은 사용자가 입력한 원본으로는 로그인이 되지 않습니다.
             따라서 원본과 패딩본을 모두 시도해야 기존 사용자가 잠기지 않습니다. */
          const pwCandidates = [...new Set([
              loginForm.password,
              loginForm.password.length < 6 ? loginForm.password.padEnd(6, '0') : loginForm.password
          ])];

          let signedIn = false;
          for (const pw of pwCandidates) {
              try {
                  await signInWithEmailAndPassword(auth, `${safeId}@imperial.com`, pw);
                  signedIn = true;
                  break;
              } catch (e) { /* 다음 후보로 계속 */ }
          }

          if (!signedIn) {
              const bridge = httpsCallable(functions, 'legacyLoginBridge');
              const res = await bridge({ userId: rawId, password: loginForm.password });
              if (!res?.data?.token) throw new Error('AUTH_FAILED');
              docId = res.data.docId || safeId;
              await signInWithCustomToken(auth, res.data.token);
          }

          /* 🔑 로그인 토큰에 담긴 역할(role) 정보를 최신으로 갱신합니다.
             보안 규칙은 이 값으로 권한을 판정하며, 이 값이 없으면 규칙이 매번
             사용자 문서를 조회하다 조회 한도에 걸립니다.
             (학생이 클리닉 시간대를 여러 개 신청할 때 권한 오류가 나던 원인) */
          try { await auth.currentUser?.getIdToken(true); } catch (e) { console.warn('토큰 갱신 실패:', e); }

          const userDocRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', docId);
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists()) {
              await signOut(auth);
              setLoginErrorModal({ isOpen: true, msg: '로그인 실패: 시스템에 등록된 계정 정보가 없습니다.' });
              return;
          }

          const docData = userDoc.data();
          if (docData.status === 'pending') {
              await signOut(auth);
              setLoginErrorModal({ isOpen: true, msg: '가입 승인이 대기 중인 계정입니다.\n\n학원 데스크에서 승인을 완료해야 로그인이 가능합니다.' });
              return;
          }

          // 혹시 남아 있을 수 있는 비밀번호 필드는 브라우저 세션에 절대 담지 않습니다.
          const { password, ...safeProfile } = docData;
          /* mustChangePassword: 서버가 비밀번호를 발급·재발급하면 세우는 표시입니다. true 일 때만 첫 로그인
             비밀번호 변경 화면이 뜹니다. 이 값은 sessionStorage 에도 함께 저장돼 새로고침(세션 복원) 뒤에도 유지됩니다. */
          const userData = {
              ...safeProfile, id: docId, authUid: auth.currentUser?.uid || safeProfile.authUid || null,
              mustChangePassword: safeProfile.mustChangePassword === true
          };

          /* 마지막 로그인 시각(lastLogin)은 더 기록하지 않는다(11-03 원장 결정 — 쓸 계획 없음).
             기록할 때마다 회원 문서가 바뀌어, 열려 있는 교직원 화면 전부가 그 문서를 다시 받고
             회원 문서 트리거 함수 3개가 매번 깨어났다. */

          setCurrentUser(userData);
          sessionStorage.setItem('imperial_user', JSON.stringify(userData));
          navigate('/dashboard');
          // '저장된 자료를 지우지 못했습니다' 안내는 직전 로그아웃 뒤 로그인 화면용입니다. 다시 들어왔으니 내립니다(다음 로그아웃 때 다시 지움).
          setCacheClearFailed(false);
          try { sessionStorage.removeItem(CACHE_CLEAR_FAILED_KEY); } catch (_) { /* 저장소 없음 */ }
      } catch (e) {
          console.error('Login Error:', e);
          try { await signOut(auth); } catch (_) {}
          const msg = e?.code === 'functions/resource-exhausted'
              ? (e.message || '로그인 시도가 많습니다. 잠시 후 다시 시도해주세요.')
              : '로그인 실패: 아이디 또는 비밀번호를 다시 확인해 주세요.';
          setLoginErrorModal({ isOpen: true, msg });
      }
      finally { setLoginProcessing(false); }
  };

  /* 로그아웃: 로그인 해제 → 이 브라우저에 남은 사본 정리(급여 사본·Firestore 사본) → 로그인 화면으로 새로 열기.
     예전에는 로그인만 풀고 화면을 바꿨습니다. 그래서 Firestore 사본(IndexedDB)에 원장이 본 급여·계좌,
     데스크가 본 학생 연락처가 그대로 남았습니다(clearLocalFirestoreCache 설명 참고). */
  const handleLogout = async () => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      /* 사본을 지우면 아직 서버에 못 보낸 변경(인터넷이 끊긴 사이 저장한 것)도 함께 사라집니다.
         예전에는 사본이 남아 같은 사람이 다시 로그인할 때 이어서 보내졌습니다. 그래서 먼저 보내 보고, 끝나지 않으면 묻습니다. */
      try {
          await withTimeout(waitForPendingWrites(db), 4000);
      } catch (e) {
          if (e?.message === 'TIMEOUT' && !window.confirm(
              '아직 서버에 저장되지 않은 변경이 있습니다(인터넷 연결이 끊겼거나 느립니다).\n\n' +
              '지금 로그아웃하면 이 기기에 남은 그 변경은 함께 지워집니다. 그래도 로그아웃할까요?\n' +
              '[취소]를 누르면 로그인한 채로 둡니다 — 연결을 확인한 뒤 다시 로그아웃해 주세요.'
          )) {
              loggingOutRef.current = false;
              return;
          }
          // 그 밖의 오류(이미 다른 탭에서 로그아웃 등)는 기다릴 변경이 없다는 뜻이라 그대로 진행합니다.
      }
      setClearingText('로그아웃하는 중입니다 — 이 기기에 남은 자료를 지우고 있습니다.');
      try { await signOut(auth); } catch (e) { console.error("Sign Out Error:", e); }
      try { sessionStorage.removeItem('imperial_user'); } catch (_) { /* 저장소 없음 */ }
      clearPayrollCache();
      setCurrentUser(null);
      const cleared = await clearLocalFirestoreCache();
      // 인터넷이 끊겨 로그인 화면을 바로 열지 못하면(openWhenOnline) 연결될 때까지 이 안내를 둡니다. 지우지 못했으면 지웠다고 쓰지 않습니다.
      if (!finishCacheClearAndReload(cleared)) {
          setClearingText(cleared
              ? `로그아웃했고 이 기기의 자료도 지웠습니다. ${OFFLINE_REOPEN_NOTICE}`
              : `로그아웃했습니다. 다만 이 기기에 저장된 자료는 지우지 못했습니다(로그인 화면이 열리면 다시 안내합니다). ${OFFLINE_REOPEN_NOTICE}`);
      }
  };

  /* 로그인 화면의 [지금 지우기] — 직전 로그아웃이 사본을 지우지 못했을 때(다른 탭이 붙잡고 있던 경우 등).
     '로그인 화면'은 이 탭에 저장된 프로필(sessionStorage)이 없다는 뜻일 뿐입니다. 로그인 상태는 같은 브라우저의 탭끼리
     공유되므로, 다른 탭에서 누가 로그인해 있으면 auth.currentUser 가 이 탭에서도 그 사람입니다. 그때 지우면 그 탭의
     Firestore 가 멈추고 아직 못 보낸 변경이 사본과 함께 지워지며, 신호로 새로고침돼 쓰던 입력도 사라집니다.
     그래서 로그인한 사람이 있으면 지우지 않고 상자 안에 알립니다. 없을 때만 기다릴 변경 없이 바로 지우고 새로 엽니다.
     로그인 처리 중(loginProcessing)에도 지우지 않습니다 — 그 로그인이 멈춘 Firestore 로 사용자 정보를 읽다 실패하지 않게. */
  const handleRetryCacheClear = async () => {
      if (loggingOutRef.current || loginProcessing) return;
      if (auth.currentUser) {
          setCacheClearBlocked(true);
          return;
      }
      setCacheClearBlocked(false);
      loggingOutRef.current = true;
      setClearingText('이 기기에 남은 자료를 지우고 있습니다.');
      const cleared = await clearLocalFirestoreCache();
      if (!finishCacheClearAndReload(cleared)) {
          setClearingText(cleared
              ? `이 기기의 자료를 지웠습니다. ${OFFLINE_REOPEN_NOTICE}`
              : `이 기기에 저장된 자료를 이번에도 지우지 못했습니다(로그인 화면이 열리면 다시 안내합니다). ${OFFLINE_REOPEN_NOTICE}`);
      }
  };
  const handleDismissCacheNotice = () => {
      setCacheClearFailed(false);
      setCacheClearBlocked(false);
      try { sessionStorage.removeItem(CACHE_CLEAR_FAILED_KEY); } catch (_) { /* 저장소 없음 */ }
  };

  /* 첫 로그인 비밀번호 변경 화면을 마쳤을 때. 화면의 사본(mustChangePassword: false)만 맞춥니다.
     비밀번호를 바꿨다면 서버의 표시도 이미 지워졌고, [이번 접속만 건너뛰기]였다면 서버의 표시는 그대로라
     다음에 새로 로그인할 때 이 화면이 다시 뜹니다(이 탭의 새로고침은 sessionStorage 사본을 쓰므로 뜨지 않음). */
  const handlePasswordChanged = (updated) => {
      try { sessionStorage.setItem('imperial_user', JSON.stringify(updated)); } catch (_) { /* 저장소 없음 — 상태만 갱신 */ }
      setCurrentUser(updated);
      navigate('/dashboard');
  };

  if (loading) return <div className="h-screen flex items-center justify-center"><Loader className="animate-spin text-blue-600" size={40} /></div>;
  if (clearingText) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 p-4 text-center">
        <Loader className="animate-spin text-blue-600" size={40} />
        <p className="max-w-md text-sm font-bold text-gray-500 leading-relaxed break-keep">{clearingText}</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/kiosk/consult" element={
        <Suspense fallback={<div className="h-screen flex items-center justify-center bg-gray-50"><Loader className="animate-spin text-blue-600" size={40} /></div>}>
          <DataProvider currentUser={KIOSK_USER}>
            <div className="w-full min-h-screen bg-gray-50 p-4 sm:p-6 md:p-10">
              <ConsultationManager isKiosk={true} />
            </div>
          </DataProvider>
        </Suspense>
      } />
      
      {/* 순서: 로그인 전 → 로그인 화면 / 첫 로그인 비밀번호 변경 표시 → 변경 화면(앱 데이터는 아직 열지 않음) / 그 밖 → 앱.
          가입 승인 대기 계정은 handleLogin 에서 이미 로그아웃되어 여기까지 오지 않습니다(기존 순서 그대로).
          상담 태블릿(/kiosk/consult)은 위의 별도 경로라 이 검사를 거치지 않습니다. */}
      <Route path="*" element={
        !currentUser ? (
          <LoginView form={loginForm} setForm={setLoginForm} onLogin={handleLogin} isLoading={loginProcessing} loginErrorModal={loginErrorModal} setLoginErrorModal={setLoginErrorModal}
            cacheClearFailed={cacheClearFailed} cacheClearBlocked={cacheClearBlocked} onRetryCacheClear={handleRetryCacheClear} onDismissCacheNotice={handleDismissCacheNotice} />
        ) : currentUser.mustChangePassword === true ? (
          <FirstPasswordChange currentUser={currentUser} onDone={handlePasswordChanged} onLogout={handleLogout} />
        ) : (
          <DataProvider currentUser={currentUser}>
            <AppLayout currentUser={currentUser} handleLogout={handleLogout} />
          </DataProvider>
        )
      } />
    </Routes>
  );
};

const App = () => <Router><AppContent /></Router>;
export default App;